import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Cog, Plane, Terminal, Play, RotateCcw, ShieldCheck } from "lucide-react";
import { EASE } from "@/lib/motion";
import { Reveal } from "@/components/Reveal";
import { SectionHeading, PreviewTag, btnPrimary } from "@/components/kit";

const AGENT_TYPES = [
    {
        id: "ai-agent",
        label: "AI AGENT",
        icon: Bot,
        tasks: ["Produce L2 bridge risk digest", "Enrich product catalog batch", "Analyze API infrastructure"],
    },
    {
        id: "robot",
        label: "ROBOT",
        icon: Cog,
        tasks: ["Warehouse inventory cycle count", "Inspect conveyor belt wear", "Dock staging verification"],
    },
    {
        id: "drone",
        label: "DRONE",
        icon: Plane,
        tasks: ["Inspect solar facility", "Survey pipeline corridor", "Assess roof damage"],
    },
    {
        id: "software-agent",
        label: "SOFTWARE AGENT",
        icon: Terminal,
        tasks: ["Stress test inference endpoint", "Automate CI failover", "Validate telemetry pipeline"],
    },
];

const RUN_STAGES = [
    { label: "TASK CREATED", log: (t) => `task registered — "${t}"` },
    { label: "AGENT ACCEPTED", log: () => `AG-0421 signed acceptance — capability match confirmed` },
    { label: "ACTION EXECUTING", log: () => `execution started — telemetry streaming` },
    { label: "EVIDENCE COLLECTED", log: () => `evidence package sealed — GPS / sensor / timestamp / logs` },
    { label: "EVIDENCE VERIFIED", log: () => `verification engine accepted 4/4 evidence items` },
    { label: "PROOF GENERATED", log: () => `proof of action written — reputation updated` },
    { label: "SETTLEMENT READY", log: () => `25 USDC release queued from escrow` },
];

export const Simulator = () => {
    const [agent, setAgent] = useState(null);
    const [task, setTask] = useState(null);
    const [step, setStep] = useState(-1);
    const timer = useRef(null);
    const running = step >= 0 && step < RUN_STAGES.length;
    const done = step >= RUN_STAGES.length;
    const selected = AGENT_TYPES.find((a) => a.id === agent);

    useEffect(() => () => clearInterval(timer.current), []);

    const run = () => {
        clearInterval(timer.current);
        setStep(0);
        timer.current = setInterval(() => {
            setStep((s) => {
                if (s + 1 >= RUN_STAGES.length) {
                    clearInterval(timer.current);
                    return RUN_STAGES.length;
                }
                return s + 1;
            });
        }, 900);
    };

    const reset = () => {
        clearInterval(timer.current);
        setStep(-1);
        setTask(null);
        setAgent(null);
    };

    return (
        <section id="simulator" data-testid="action-simulator" className="section relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#07111F]/60 to-transparent" aria-hidden="true" />
            <div className="shell relative">
                <Reveal>
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <SectionHeading
                            index="03"
                            eyebrow="INTERACTIVE SIMULATOR"
                            title={
                                <>
                                    SEE AN ACTION
                                    <br />
                                    BECOME <span className="text-[#1677FF]">PROOF.</span>
                                </>
                            }
                        />
                        <PreviewTag>SIMULATION</PreviewTag>
                    </div>
                </Reveal>

                <Reveal delay={0.1} className="mt-12">
                    <div className="grid gap-px border border-white/[0.08] bg-white/[0.06] lg:grid-cols-[380px_1fr]">
                        {/* LEFT — selection */}
                        <div className="bg-[#07111F] p-7 md:p-9">
                            <p className="label-tech">STEP 01 — CHOOSE AGENT TYPE</p>
                            <div className="mt-4 grid grid-cols-2 gap-2.5">
                                {AGENT_TYPES.map((a) => (
                                    <button
                                        key={a.id}
                                        data-testid={`simulator-agent-${a.id}`}
                                        onClick={() => {
                                            setAgent(a.id);
                                            setTask(null);
                                            setStep(-1);
                                        }}
                                        aria-pressed={agent === a.id}
                                        className={`flex flex-col items-start gap-3 border p-4 text-left transition-all duration-300 ${
                                            agent === a.id
                                                ? "border-[#1677FF] bg-[#1677FF]/10 text-ink"
                                                : "border-white/10 bg-[#0A1627] text-mute hover:border-[#1677FF]/50"
                                        }`}
                                    >
                                        <a.icon size={18} className={agent === a.id ? "text-[#31D8FF]" : ""} />
                                        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]">{a.label}</span>
                                    </button>
                                ))}
                            </div>

                            <p className="label-tech mt-8">STEP 02 — CHOOSE TASK</p>
                            <div className="mt-4 space-y-2" data-testid="simulator-task-list">
                                {selected ? (
                                    selected.tasks.map((t) => (
                                        <button
                                            key={t}
                                            data-testid={`simulator-task-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            onClick={() => {
                                                setTask(t);
                                                setStep(-1);
                                                clearInterval(timer.current);
                                            }}
                                            aria-pressed={task === t}
                                            className={`flex w-full items-center justify-between gap-3 border px-4 py-3.5 text-left font-mono text-[11px] uppercase tracking-[0.14em] transition-all duration-300 ${
                                                task === t
                                                    ? "border-[#1677FF] bg-[#1677FF]/10 text-ink"
                                                    : "border-white/10 bg-[#0A1627] text-mute hover:border-[#1677FF]/50"
                                            }`}
                                        >
                                            {t}
                                            <span className={`h-1.5 w-1.5 rounded-full ${task === t ? "bg-[#31D8FF]" : "bg-white/20"}`} />
                                        </button>
                                    ))
                                ) : (
                                    <p className="border border-dashed border-white/10 px-4 py-6 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-mute/60">
                                        SELECT AN AGENT TYPE TO LIST TASKS
                                    </p>
                                )}
                            </div>

                            <div className="mt-8 flex gap-3">
                                <button
                                    data-testid="simulator-run-button"
                                    onClick={run}
                                    disabled={!task || running}
                                    className={`${btnPrimary} flex-1 px-5`}
                                >
                                    <Play size={13} /> {running ? "RUNNING" : "RUN SIMULATION"}
                                </button>
                                {step > -1 && (
                                    <button data-testid="simulator-reset" onClick={reset} aria-label="Reset simulation" className={`${btnPrimary} bg-transparent border border-white/15 text-ink hover:bg-white/5`}>
                                        <RotateCcw size={13} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* RIGHT — console */}
                        <div className="relative bg-[#040811] p-7 md:p-9">
                            <div className="flex items-center justify-between border-b border-white/[0.08] pb-4">
                                <p className="label-tech">VERIFICATION CONSOLE</p>
                                <span className="font-mono text-[10px] tracking-[0.2em] text-mute">
                                    {task ? `TASK: ${task.toUpperCase()}` : "NO TASK SELECTED"}
                                </span>
                            </div>

                            <div className="mt-2 min-h-[210px] space-y-2.5 pt-5" data-testid="simulator-terminal">
                                {step < 0 && (
                                    <p className="font-mono text-xs leading-loose text-mute/60">
                                        &gt; ACTRA simulation environment — PROTOCOL PREVIEW
                                        <br />
                                        &gt; select an agent type and a task, then run the simulation
                                        <span className="animate-blink text-[#31D8FF]">▊</span>
                                    </p>
                                )}
                                {RUN_STAGES.slice(0, Math.max(step + 1, 0)).map((s, i) => (
                                    <motion.p
                                        key={s.label}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, ease: EASE }}
                                        className="font-mono text-xs text-mute"
                                    >
                                        <span className="text-[#31D8FF]">[{String(i).padStart(2, "0")}:0{(i + 1) * 8 % 10}]</span>{" "}
                                        <span className="text-ink">{s.label}</span> — {s.log(task)}
                                    </motion.p>
                                ))}
                            </div>

                            {/* Progress */}
                            <div className="mt-6">
                                <div className="h-1 w-full bg-white/[0.06]">
                                    <motion.div
                                        className="h-full bg-[#1677FF]"
                                        animate={{ width: `${((step + 1) / (RUN_STAGES.length + 1)) * 100}%` }}
                                        transition={{ duration: 0.5, ease: EASE }}
                                    />
                                </div>
                                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                                    {RUN_STAGES.map((s, i) => (
                                        <span
                                            key={s.label}
                                            className={`font-mono text-[9px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                                                i <= step ? "text-[#31D8FF]" : "text-mute/40"
                                            }`}
                                        >
                                            {s.label}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <AnimatePresence>
                                {done && (
                                    <motion.div
                                        data-testid="simulator-verdict"
                                        initial={{ opacity: 0, scale: 0.96 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.5, ease: EASE }}
                                        className="relative mt-8 border border-[#1677FF]/50 bg-[#1677FF]/[0.07] p-6"
                                    >
                                        <span className="absolute -top-2.5 left-6 bg-[#040811] px-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#31D8FF]">
                                            SIMULATION RESULT
                                        </span>
                                        <div className="flex flex-wrap items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <ShieldCheck size={30} className="text-[#31D8FF]" />
                                                <div>
                                                    <p className="font-display text-2xl font-bold uppercase tracking-tightest text-ink">
                                                        ACTION VERIFIED
                                                    </p>
                                                    <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-mute">
                                                        PROOF ID: ACTRA-8F72-91AC · 25 USDC — SETTLEMENT READY
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-mute/70">
                                                SIMULATION OUTPUT — NOT A REAL PROOF
                                            </p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default Simulator;
